const { useRef, useState } = require('react');
const T = require('prop-types');
const { NavLink: Link } = require('react-router-dom');
const { replace: Replace } = require('connected-react-router');
const { default: Styled } = require('styled-components');
const { useDispatch } = require('react-redux');

const { default: AddIcon } = require('@mui/icons-material/Add');
const { default: Avatar } = require('@mui/material/Avatar');
const { default: FAB } = require('@mui/material/Fab');
const { default: MuiButton } = require('@mui/material/Button');
const { default: ChatIcon } = require('@mui/icons-material/Chat');
const { default: GroupIcon } = require('@mui/icons-material/Group');
const { default: SettingsIcon } = require('@mui/icons-material/Settings');
const { default: PeopleIcon } = require('@mui/icons-material/People');

const Emoji = require('components/Emoji');
const MaterialMenu = require('components/MaterialMenu');
const RadioButtonGroup = require('components/RadioButtonGroup');
const RadioButton = require('components/RadioButton');
const { groupChatLink, groupDetailsLink } = require('utils/groups');
const { MAX_CONTENT_WIDTH, MOBILE_MAX_CONTENT_WIDTH } = require('utils/constants');
const { transient$Props } = require('styles/utils');

const { context } = require('app-context');
const { useAppSelector } = require('utils/redux');
const { useMediaQuery } = require('../../../../hooks/useMediaQuery');

const {
    actions,
    selectors: {
        all: selectors
    }
} = context;

const internals = {};

module.exports = function GroupHeader(props) {

    const { group, variant, openSimpleDialogWithProps } = props;

    const {
        Container,
        HeaderContent,
        HeaderAvatar,
        HeaderTitleArea,
        HeaderTitle,
        HeaderDetails,
        DetailLine,
        HeaderButton,
        ButtonsContainer,
        NotificationSettings,
        SettingsIconButton
    } = internals;

    const {
        isAnnouncement,
        notificationLevel,
        emojiSymbol,
        name,
        private: classIsPrivate,
        facilitator,
        professor,
        campus,
        location,
        room,
        shortDescription,
        additionalDescription
    } = group;
    const isChatHeader = variant === 'chat';

    const dispatch = useDispatch();
    const headerNotificationSettings = useRef(notificationLevel);

    const showJoin = useAppSelector(selectors.getCanJoinClass, group.id);

    const {
        canEditGroups,
        canSeeChatMessagesNoJoin
    } = useAppSelector(selectors.getCurrentUserRolePermissions);

    const menuButtonRef = useRef(null);

    const isWide = useMediaQuery('screen and (min-width: 600px)');

    const leaveGroup = () => {

        openSimpleDialogWithProps({
            title: 'Leave Group',
            message: `Are you sure you want to leave group "${group.name}"?`,
            declineLabel: 'No',
            confirmLabel: 'Yes',
            confirm: async () => {
                await dispatch(actions.classes.leave(group));
                dispatch(Replace('/app/classes'));
            }
        });
    };

    const editGroup = () => {

        dispatch(actions.router.push(`/app/classes/${group.id}/edit`));
    };

    const openNotificationSettings = () => {

        openSimpleDialogWithProps({
            title: 'Activity Notification',
            message: (
                <NotificationSettings
                    headerNotificationSettingsRef={headerNotificationSettings}
                    notificationLevel={notificationLevel}
                />
            ),
            declineLabel: 'Cancel',
            confirmLabel: 'Save',
            confirm: async () => {

                await dispatch(actions.classes.updateNotificationLevel({
                    id: group.id,
                    notificationLevel: headerNotificationSettings.current
                }));
            }
        });
    };

    const menuOptions = [];

    if (canEditGroups) {
        menuOptions.push({
            name: 'Edit Group',
            action: editGroup,
            ariaRole: 'button',
            ariaLabel: 'Edit Group'
        });
    }

    if (!isAnnouncement) {
        menuOptions.push({
            name: 'Notification Settings',
            action: openNotificationSettings,
            ariaRole: 'button',
            ariaLabel: 'Notification Settings'
        });
    }

    if (!showJoin) {
        menuOptions.push({
            name: 'Leave Group',
            action: leaveGroup,
            ariaRole: 'button',
            ariaLabel: 'Leave Group'
        });
    }

    const showSecondButton = showJoin
        && canSeeChatMessagesNoJoin
        && (group.sid || (isAnnouncement && group.id));

    const detailLines = [];

    if (!isAnnouncement && classIsPrivate) {
        detailLines.push(<DetailLine key='private' $muted>(PRIVATE)</DetailLine>);
    }

    if (isAnnouncement) {
        detailLines.push(<DetailLine key='announcement' $muted>(ANNOUNCEMENTS ONLY)</DetailLine>);
    }

    if (facilitator) {
        detailLines.push(<DetailLine key='facilitator'><b>Facilitator:</b>&nbsp;{facilitator}</DetailLine>);
    }

    if (professor) {
        detailLines.push(<DetailLine key='professor'><b>Professor:</b>&nbsp;{professor}</DetailLine>);
    }

    if (campus) {
        detailLines.push(<DetailLine key='campus'><b>Campus:</b>&nbsp;{campus}</DetailLine>);
    }

    if (location) {
        detailLines.push(<DetailLine key='location'><b>Location:</b>&nbsp;{location}{room && ` ${room}`}</DetailLine>);
    }

    if (shortDescription) {
        detailLines.push(<DetailLine key='short-description'>{shortDescription}</DetailLine>);
    }

    if (additionalDescription) {
        detailLines.push(<DetailLine key='additional-description'>{additionalDescription}</DetailLine>);
    }

    const MiddleActionButtons = (
        <ButtonsContainer $isChatHeader={isChatHeader}>
            {(!showJoin || canSeeChatMessagesNoJoin) && <MaterialMenu
                anchorOrigin={{ horizontal: 'left' }}
                buttonRef={menuButtonRef}
                data-focus-outline='radius:20,padding:3'
                iconElement={(
                    <SettingsIconButton>
                        <SettingsIcon />
                    </SettingsIconButton>
                )}
                menuOptions={menuOptions}
            />}
            <HeaderButton
                group={group}
                isWide={isWide}
                showJoin={showJoin}
                isAnnouncement={isAnnouncement}
                variant={variant}
            />
            {showSecondButton && (
                <HeaderButton
                    group={group}
                    isWide={isWide}
                    showJoin={false}
                    isAnnouncement={isAnnouncement}
                    variant={variant}
                />
            )}
        </ButtonsContainer>
    );

    let LeftIcon = GroupIcon;

    if (emojiSymbol) {
        // eslint-disable-next-line react/display-name
        LeftIcon = (iconProps) => <Emoji {...iconProps} symbol={emojiSymbol} />;
    }

    return (
        <Container $isChatHeader={isChatHeader}>
            <HeaderContent $isChatHeader={isChatHeader}>
                <HeaderAvatar>
                    <Avatar>
                        <LeftIcon />
                    </Avatar>
                </HeaderAvatar>
                <HeaderTitleArea $isChatHeader={isChatHeader}>
                    <HeaderTitle>{name}</HeaderTitle>
                    {!isChatHeader && !!detailLines.length && <HeaderDetails>{detailLines}</HeaderDetails>}
                </HeaderTitleArea>
                {MiddleActionButtons}
            </HeaderContent>
        </Container>
    );
};

module.exports.propTypes = {
    group: T.object,
    variant: T.oneOf(['chat', 'details']),
    openSimpleDialogWithProps: T.func.isRequired
};

internals.HeaderButton = function HeaderButton(props) {

    const {
        group,
        isWide,
        showJoin,
        isAnnouncement,
        variant
    } = props;

    const {
        Button,
        HeaderFAB
    } = internals;

    const dispatch = useDispatch();
    const isChatHeader = variant === 'chat';

    const joinGroup = () => {

        dispatch(actions.classes.join(group));
    };

    let btnText = '';
    let ariaLabel = '';
    let contrast = false;
    let FABIcon = ChatIcon;
    const buttonProps = {};

    if (showJoin) {
        btnText = 'Join';
        contrast = true;
        FABIcon = AddIcon;
        buttonProps.onClick = joinGroup;
    }
    else {
        buttonProps.component = Link;

        if (variant === 'chat') {
            btnText = 'Details';
            ariaLabel = 'Go to Group Details';
            FABIcon = PeopleIcon;
            buttonProps.to = groupDetailsLink(group);
        }
        else {
            // On group members page
            buttonProps.to = groupChatLink(group);

            if (isAnnouncement) {
                btnText = 'Posts';
                ariaLabel = 'Go to Group Posts';
                contrast = true;
            }
            else {
                btnText = 'Chat';
                ariaLabel = 'Go to Group Chat';
            }
        }
    }

    if (!isWide) {
        return (
            <HeaderFAB
                mini
                {...buttonProps}
                $contrast={contrast}
                $isChatHeader={isChatHeader}
                data-focus-outline='radius:40,padding:4'
            >
                <FABIcon style={{ color: contrast ? '#fff' : '#000' }} />
            </HeaderFAB>
        );
    }

    return (
        <Button
            {...buttonProps}
            variant='contained'
            size='small'
            aria-label={ariaLabel}
            $isAnnouncement={isAnnouncement}
            $contrast={contrast}
            $isChatHeader={isChatHeader}
            data-focus-outline='radius:40,padding:4'
        >
            {btnText}
        </Button>
    );
};

internals.HeaderButton.propTypes = {
    group: T.object,
    isWide: T.bool,
    showJoin: T.bool,
    isAnnouncement: T.bool,
    variant: T.oneOf(['chat', 'details'])
};

internals.Container = Styled('div', transient$Props)`
    background-color: ${({ theme }) => theme.palette.primary.main};
    overflow: hidden;
    height: auto;
    width: 100%;
    max-width: ${MAX_CONTENT_WIDTH}px;
    margin-right: auto;
    margin-left: auto;
    box-sizing: border-box;
    position: relative;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;

    @media screen and (max-width: 600px) {
        max-width: ${MOBILE_MAX_CONTENT_WIDTH}px;
    }
`;

internals.HeaderContent = Styled('div')`
    display: grid;
    grid-template-columns: 62px minmax(0, 1fr) auto;
    column-gap: 10px;
    align-items: start;
    width: 100%;
    padding: 14px 12px;
    box-sizing: border-box;
    position: relative;

    @media screen and (max-width: 600px) {
        grid-template-columns: 50px minmax(0, 1fr) auto;
        column-gap: 6px;
        padding: 12px 8px;
    }
`;

internals.HeaderAvatar = Styled('div')`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    min-width: 0;

    .MuiAvatar-root {
        width: 52px;
        height: 52px;
        background: #ffffff;
        color: #000000;
        box-sizing: border-box;

        font-size: 38px;
    }

    @media screen and (max-width: 600px) {
        .MuiAvatar-root {
            width: 50px;
            height: 50px;
        }
    }
`;

internals.HeaderTitleArea = Styled('div')`
    grid-column: 2;
    min-width: 0;
    width: 100%;
    padding-top: 4px;
`;

internals.HeaderTitle = Styled('h1')`
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    font-size: 24px;
    line-height: 1.08;
    font-weight: 500;
    overflow-wrap: anywhere;

    @media screen and (max-width: 600px) {
        font-size: 22px;
    }
`;

internals.HeaderDetails = Styled('div')`
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.78);
    font-size: 14px;
    line-height: 18px;
`;

internals.DetailLine = Styled('div', transient$Props)`
    margin-top: 3px;
    font-style: ${({ $muted }) => $muted ? 'italic' : 'normal'};
`;

internals.ButtonsContainer = Styled('div')`
    grid-column: 3;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    width: 86px;
    min-width: 0;
    z-index: 1;
    padding-top: ${({ $isChatHeader }) => $isChatHeader ? '2px' : '4px'};

    @media screen and (max-width: 600px) {
        width: 42px;
        gap: 10px;
        padding-top: 0;
    }
`;

internals.SettingsIconButton = Styled('div')`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.14);
    box-sizing: border-box;

    svg {
        width: 25px;
        height: 25px;
    }

    @media screen and (max-width: 600px) {
        width: 36px;
        height: 36px;

        svg {
            width: 24px;
            height: 24px;
        }
    }
`;

internals.HeaderFAB = Styled(FAB, transient$Props)`
    width: 38px;
    height: 38px;
    min-height: 38px;
    margin: 0;
    border-radius: 50%;
    background-color: ${({ theme, $contrast }) => $contrast ? theme.palette.secondary.main : 'rgb(221, 251, 255)'};
    color: ${({ $contrast }) => $contrast ? '#fff' : '#000'};
    &:hover {
        background-color: ${({ theme, $contrast }) => $contrast ? theme.palette.secondary.main : 'rgb(192, 244, 248)'};
    }

    svg {
        font-size: 24px;
    }

    @media screen and (max-width: 600px) {
        width: 36px;
        height: 36px;
        min-height: 36px;

        svg {
            font-size: 23px;
        }
    }
`;

internals.Button = Styled(MuiButton, transient$Props)`
    transition: background-color 0.4s;
    background-color: ${({ theme, $contrast }) => $contrast ? theme.palette.secondary.main : 'rgb(221, 251, 255)'};
    color: ${({ $contrast }) => $contrast ? '#fff' : '#000'};
    min-width: 72px;
    min-height: 30px;
    padding-right: 12px;
    padding-left: 12px;
    line-height: 1;
    &:hover {
        background-color: ${({ theme, $contrast }) => $contrast ? theme.palette.secondary.main : 'rgb(192, 244, 248)'};
    }
`;

internals.NotificationSettings = function GroupNotificationSettings(props) {

    const {
        headerNotificationSettingsRef,
        notificationLevel
    } = props;

    const [notificationSettings, setNotificationSettings] = useState(notificationLevel);

    const setRadioVal = (field) => {

        return (ev, ...a) => {

            const { value } = ev.target;

            switch (field) {
                case 'notification-settings':
                    setNotificationSettings(value);
                    headerNotificationSettingsRef.current = value;
                    return;
            }
        };
    };

    return (<div>
        <RadioButtonGroup
            name='notification-settings'
            labelId='notification-settings-label'
            valueSelected={notificationSettings}
            onChange={setRadioVal('notification-settings')}
        >
            <RadioButton
                value='every'
                label='Unmuted'
                aria-label='Unmuted'
                dataFocusOutline='radius:30,padding:-4'
            />
            <RadioButton
                value='muted'
                label='Muted'
                aria-label='Muted'
                dataFocusOutline='radius:30,padding:-4'
            />
        </RadioButtonGroup>
    </div>);
};

internals.NotificationSettings.propTypes = {
    headerNotificationSettingsRef: T.object,
    notificationLevel: T.string
};
